import React from 'react';
import styled from '@emotion/styled';
import { getSubCategoryToShow } from '@productSearch/components/Cards/ResultCard/resultCard.utils';
import { IconButton } from 'components/Buttons';
import { Button } from 'components/Buttons/Button';
import { theme } from 'theme';
import pxToEm from 'theme/pxToEm';

import { CardInfo } from 'ui-widgets/components/Cards/ProductCard/CardInfo';
import { MiniCardProps } from '../RecommendStripUI.types';
import { Features } from './Features';
import { Image } from './Image';

export const MiniCard: React.FC<MiniCardProps> = ({
  product,
  cardButtonsLabels,
  isAddToCartLoading,
  isSelected,
  showSecondaryActionButton = false,
  onAddToCart,
  onCardClick,
}) => {
  const handleActionButtonClick = (e: React.SyntheticEvent) => {
    if (product.isProduct || product.isDigital) {
      onCardClick?.(product);
    } else {
      e.stopPropagation();
      onAddToCart?.(product);
    }
  };

  const labels = {
    addToCart: cardButtonsLabels?.addToCart || 'Add to Cart',
    learnMore: cardButtonsLabels?.learnMore || 'Learn More',
    sampleFinishes: cardButtonsLabels?.sampleFinishes || 'Sample Finishes',
  };

  const isProduct =
    product.isProduct || product?.productFinishType?.includes('Product');
  const isDigital = product.isDigital;

  const name = product?.taxonomyL2?.[0] || product.name;
  const buttonLabel = isProduct
    ? labels.sampleFinishes
    : isDigital
    ? labels.learnMore
    : labels.addToCart;

  // copied from ProductCard.tsx
  const taxonomyLabel = product?.taxonomy?.[0]?.split('>')?.pop();
  const subtitle1 = getSubCategoryToShow(product) ?? taxonomyLabel;
  const usageList =
    product &&
    product?.residentialCommercial?.length &&
    product?.residentialCommercial?.length > 0
      ? product.residentialCommercial
      : null;
  const usageType =
    usageList?.map((usage: string) => usage?.split(' ')[0]) || [];

  const features = [subtitle1, ...usageType].filter((item) => Boolean(item));

  return (
    <CardWrapper
      isActive={isAddToCartLoading}
      isSelected={isSelected}
      onClick={() => onCardClick?.(product)}
    >
      <ImageWrapper isSelected={isSelected}>
        <Image
          productImageUrl={product.thumbnailImageUrl}
          title={name}
          isSelected={isSelected}
        />
      </ImageWrapper>
      {showSecondaryActionButton && (
        <ContainedCartButton
          onClick={handleActionButtonClick}
          isLoading={isAddToCartLoading}
          dense
          iconName="cart-material"
          color="secondary"
          size="large"
        />
      )}
      {!showSecondaryActionButton && (
        <HoverContainer>
          {features.length ? <Features features={features} /> : null}
          <CartButton
            fullWidth
            title={buttonLabel}
            variant="contained"
            color="secondary"
            className="inline-block truncate"
            disabled={isAddToCartLoading}
            onClick={handleActionButtonClick}
          >
            {buttonLabel}
          </CartButton>
        </HoverContainer>
      )}
      <div className="mt-2">
        <CardInfo
          title={product?.manufacturer}
          subtitle1={subtitle1}
          subtitle2={name}
          categoryName={'categoryName'}
          brandName={'brandName'}
        />
      </div>
    </CardWrapper>
  );
};

/////

const ImageWrapper = styled.div<{ isSelected?: boolean }>`
  display: flex;
  width: 114px;
  height: 114px;
  background-color: ${theme.palette.secondary.main};
  object-fit: cover;
  border-radius: 24px;
  box-sizing: border-box;

  ${({ isSelected }) =>
    isSelected &&
    `
        border: 2px solid ${theme.palette.primary.main};
        border-radius: 24px;
    `}
`;

const HoverContainer = styled.div`
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 114px;
  z-index: 3;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 24px;
`;

const CartButton = styled(Button)`
  width: 90%;
  max-width: 100px;
  bottom: 8px;

  font-size: 12px;
  &.Mui-disabled {
    background-color: ${theme.palette.secondary?.main};
    color: ${theme.palette.primary.main};
    opacity: 0.5;
  }
`;

const CardWrapper = styled.div<{
  isActive?: boolean;
  isSelected?: boolean;
}>`
  display: flex;
  flex-direction: column;
  position: relative;
  cursor: pointer;
  width: 114px;
  height: 180px;
  box-sizing: border-box;
  &:hover {
    ${HoverContainer} {
      display: flex;
    }
  }
  ${({ isActive }) =>
    isActive &&
    `
            ${HoverContainer} {
            display: flex;
        }
    `}
`;

const ContainedCartButton = styled(IconButton)`
  position: absolute;
  width: ${pxToEm(32)};
  height: ${pxToEm(32)};
  right: ${pxToEm(8)};
  top: 74px;
  z-index: 2;
`;
